// constants
import { StoreStorageSyncKeys } from 'components/mrp/mrp.consts';

// types
import { CalendarSourceData, CalendarStore } from './calendarStore.types';

// utils
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getNewCalendarId, recalculateMappings } from './calendarStore.utils';
import dayjs from 'dayjs';
import { getSortedCalendars } from 'components/mrp/mrp.utils';

export const useCalendarStore = create<CalendarStore>()(
  persist(
    (set) => ({
      calendars: {},
      calendarIdsPerEntityId: {},
      entityIdsPerCalendarId: {},
      hasHydrated: false,
      timeSlotMin: dayjs()
        .startOf('day')
        .hour(7)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate(), // 7:00
      timeSlotMax: dayjs()
        .startOf('day')
        .hour(19)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate(), // 19:00
      timeStep: 30,
      viewState: 'APPOINTMENT',
      control: {
        openCalendar: (sources, asPinned) =>
          set((state) => {
            const sourcesEntityIds = sources.map(
              (x) => x.planningEntityIdentifier.entityId!
            );

            const alreadyOpened = Object.values(state.calendars).some(
              (calendar) => {
                if (calendar.sources.length !== sources.length) return false;

                const calendarEntityIds = calendar.sources.map(
                  ({ planningEntityIdentifier }) =>
                    planningEntityIdentifier.entityId!
                );

                return sourcesEntityIds.every(
                  (srcId) => calendarEntityIds.indexOf(srcId) != -1
                );
              }
            );

            // Don't open calendars that are already opened
            if (alreadyOpened) return state;

            const currentOrderIdx = Math.max(
              // Pass 0 to ensure the IDX always returns a value
              0,
              ...Object.values(state.calendars).map(
                (calendar) => calendar.order || 0
              )
            );

            const calendar = {
              calendarId: getNewCalendarId(Object.keys(state.calendars)),
              sources,
              isPinned: asPinned,
              order: currentOrderIdx + 1
            };

            const calendars = {
              ...state.calendars,
              [calendar.calendarId]: calendar
            };

            const { calendarIdsPerEntityId, entityIdsPerCalendarId } =
              recalculateMappings(calendars);

            return {
              ...state,
              calendars,
              calendarIdsPerEntityId,
              entityIdsPerCalendarId
            };
          }),
        closeCalendar: (calendarId, forceClose) =>
          set((state) => {
            const targetCalendar = state.calendars?.[calendarId];

            // Check if calendar can/should be closed
            if (
              !targetCalendar || // Calendar couldnt be found
              (targetCalendar.isPinned && !forceClose) // Calendar was pinned and force is false
            )
              return state;

            // Close the calendar
            const updatedCalendars = Object.fromEntries(
              Object.entries(state.calendars)
                .filter(([id]) => id != calendarId)
                .map(([id, calendar]) => [id, calendar])
            );

            const { calendarIdsPerEntityId, entityIdsPerCalendarId } =
              recalculateMappings(updatedCalendars);

            return {
              ...state,
              calendars: updatedCalendars,
              calendarIdsPerEntityId,
              entityIdsPerCalendarId
            };
          }),
        replaceCalendar: (
          targetCalendarId: string,
          sources: CalendarSourceData[]
        ) =>
          set((state) => {
            const targetCalendar = state.calendars?.[targetCalendarId];

            if (!targetCalendar) return state;

            // Replace the calendar with a new one
            const newCalendar = {
              ...targetCalendar,
              calendarId: getNewCalendarId(Object.keys(state.calendars)),
              sources,
              order: targetCalendar.order || Object.keys(state.calendars).length
            };

            const updatedCalendars = {
              ...state.calendars,
              [newCalendar.calendarId]: newCalendar
            };

            // Remove the old calendar
            delete updatedCalendars[targetCalendarId];

            const { calendarIdsPerEntityId, entityIdsPerCalendarId } =
              recalculateMappings(updatedCalendars);

            return {
              ...state,
              calendars: updatedCalendars,
              calendarIdsPerEntityId,
              entityIdsPerCalendarId
            };
          }),
        mergeCalendars: (calendarIds) =>
          set((state) => {
            const targetCalendars = Object.values(state.calendars).filter(
              ({ calendarId }) => calendarIds.indexOf(calendarId) != -1
            );

            // Cannot merge less than 2 calendars
            if (targetCalendars.length < 2) return state;

            // We select the first calendar in the array
            // to be the one all other calendars will be merged into.
            const firstCalendar = targetCalendars.filter(
              ({ calendarId }) => calendarId === calendarIds[0]
            )[0];

            // Merge all the data into the first calendar.
            const newCalendar = (state.calendars[firstCalendar.calendarId] = {
              calendarId: firstCalendar.calendarId,
              sources: targetCalendars.flatMap(({ sources }) => sources),
              isPinned: true,
              order: firstCalendar.order
            });

            const calendarIdsToRemove = calendarIds.slice(1);

            const otherCalendars = Object.fromEntries(
              Object.entries(state.calendars).filter(
                ([, calendar]) =>
                  calendarIdsToRemove.indexOf(calendar.calendarId) === -1
              )
            );

            const newCalendars = {
              ...otherCalendars,
              [firstCalendar.calendarId]: newCalendar
            };

            const { calendarIdsPerEntityId, entityIdsPerCalendarId } =
              recalculateMappings(newCalendars);

            return {
              ...state,
              calendars: newCalendars,
              calendarIdsPerEntityId,
              entityIdsPerCalendarId
            };
          }),
        splitCalendar: (calendarId, entityId) =>
          set((state) => {
            const targetCalendar = state.calendars[calendarId] || null;
            if (!targetCalendar) return state;

            const currentOrderIdx = Math.max(
              // Pass 0 to ensure the IDX always returns a value
              0,
              ...Object.values(state.calendars).map(
                (calendar) => calendar.order || 0
              )
            );

            // If no entityId is supplied, we split out the entire
            // merged calendar
            if (!entityId) {
              // Remove the old calendar
              delete state.calendars[calendarId];

              targetCalendar.sources
                .map((src, idx) => ({
                  calendarId: getNewCalendarId(Object.keys(state.calendars)),
                  sources: [src],
                  isPinned: true,
                  order: currentOrderIdx + 1 + idx
                }))
                .forEach((calendar) => {
                  state.calendars[calendar.calendarId] = calendar;
                });
            } else {
              const entitySources = targetCalendar.sources.filter(
                ({ planningEntityIdentifier }) =>
                  planningEntityIdentifier.entityId === entityId
              );

              if (entitySources.length === 0) return state;

              targetCalendar.sources = targetCalendar.sources.filter(
                ({ planningEntityIdentifier }) =>
                  planningEntityIdentifier.entityId !== entityId
              );

              const calendar = {
                calendarId: getNewCalendarId(Object.keys(state.calendars)),
                sources: entitySources,
                isPinned: true,
                order: currentOrderIdx + 1
              };

              state.calendars[targetCalendar.calendarId] = targetCalendar;
              state.calendars[calendar.calendarId] = calendar;
            }

            const { calendarIdsPerEntityId, entityIdsPerCalendarId } =
              recalculateMappings(state.calendars);

            return {
              ...state,
              calendarIdsPerEntityId,
              entityIdsPerCalendarId
            };
          }),
        setTimeSlotMinMax: (type: 'min' | 'max', newValue: Date) =>
          set((state) => {
            const d = dayjs(newValue);
            // Guard against setting min/max outside of the current time slot range
            if (
              !d.isSame(
                type === 'min' ? state.timeSlotMin : state.timeSlotMax,
                'day'
              )
            ) {
              return state;
            }

            return {
              ...state,
              ...(type === 'min'
                ? { timeSlotMin: newValue }
                : type === 'max'
                ? { timeSlotMax: newValue }
                : {})
            };
          }),
        setTimeStep: (timeStep) =>
          set((state) => {
            return {
              ...state,
              timeStep
            };
          }),
        toggleCalendarPin: (calendarId) =>
          set((state) => {
            const targetCalendar = state.calendars[calendarId];

            if (!targetCalendar) return state;

            return {
              ...state,
              calendars: {
                ...state.calendars,
                [calendarId]: {
                  ...targetCalendar,
                  isPinned: !targetCalendar.isPinned
                }
              }
            };
          }),
        moveCalendar: (calendarId, newPosition) =>
          set((state) => {
            const targetCalendar = state.calendars[calendarId];

            if (!targetCalendar) return state;

            if (newPosition === 'MAX') {
              const currentOrderIdx = Math.max(
                // Pass 0 to ensure the IDX always returns a value
                0,
                ...Object.values(state.calendars).map(
                  (calendar) => calendar.order || 0
                )
              );

              return {
                ...state,
                calendars: {
                  ...state.calendars,
                  [calendarId]: {
                    ...targetCalendar,
                    order: currentOrderIdx + 1
                  }
                }
              };
            } else if (newPosition === 'MIN') {
              const allCalendars = state.calendars;

              Object.entries(allCalendars)
                .map(([id, calendar]) => ({
                  newPosition: (calendar.order || 0) + 1,
                  id
                }))
                .forEach(({ id, newPosition }) => {
                  allCalendars[id] = {
                    ...allCalendars[id],
                    order: newPosition
                  };
                });

              allCalendars[calendarId] = {
                ...allCalendars[calendarId],
                order: 0
              };

              return {
                ...state,
                calendars: allCalendars
              };
            } else {
              const sortCalendars = getSortedCalendars(state.calendars);

              const currentCalendarIdx = sortCalendars.indexOf(targetCalendar);
              const adjacentCalendar =
                sortCalendars[currentCalendarIdx + newPosition];

              return {
                ...state,
                calendars: {
                  ...state.calendars,
                  [calendarId]: {
                    ...targetCalendar,
                    order: adjacentCalendar.order
                  },
                  [adjacentCalendar.calendarId]: {
                    ...adjacentCalendar,
                    order: targetCalendar.order
                  }
                }
              };
            }
          }),
        setViewState: (viewState) => set(() => ({ viewState }))
      }
    }),
    {
      name: StoreStorageSyncKeys.CALENDAR_STORE,
      partialize: (state) => ({
        calendars: state.calendars,
        calendarIdsPerEntityId: state.calendarIdsPerEntityId,
        entityIdsPerCalendarId: state.entityIdsPerCalendarId,
        timeSlotMin: state.timeSlotMin,
        timeSlotMax: state.timeSlotMax,
        timeStep: state.timeStep
      }),
      merge: (persistedState, currentState) => {
        const untransformedState = persistedState as Partial<CalendarStore>;

        return {
          ...currentState,
          ...untransformedState,
          // Transform persisted date strings back to Dayjs objects
          timeSlotMin: dayjs(untransformedState?.timeSlotMin).isValid()
            ? dayjs(untransformedState.timeSlotMin).toDate()
            : currentState.timeSlotMin,
          timeSlotMax: dayjs(untransformedState?.timeSlotMax).isValid()
            ? dayjs(untransformedState.timeSlotMax).toDate()
            : currentState.timeSlotMax
        };
      },
      onRehydrateStorage: (state) => {
        state.hasHydrated = true;
      },
      version: 1
    }
  )
);
